import React from 'react'
import { Box, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { Sparkline } from './Sparkline'
import type { BoatDetailData, ServiceEvent } from './ServiceTable'

const ALARM_COLOR = '#F59E0B'
const DTC_COLOR = '#E00000'
const ENGINE_COLOR = '#508991' // teal — matches the app accent

/**
 * Metrics tab for an expanded Service-tab boat: a combined engine-hour line +
 * alarm/DTC dots sparkline (rich hover = day's engine-hour min/max + fault log).
 */
const MetricsPanel: React.FC<{
    detail: BoatDetailData
    days?: string[]
}> = ({ detail, days }) => {
    const hoursByDay = detail.hoursByDay || {}
    const events = detail.events || []

    // Day axis: prefer the window axis; else the days we have telemetry for.
    const axis =
        days && days.length ? days : Object.keys(hoursByDay).sort()

    // Lifetime engine hours trend upward; carry the last known reading across
    // gap days so the line stays continuous. Leading gap → first known value.
    const firstKnown = axis
        .map((d) => hoursByDay[d]?.hi)
        .find((v) => v != null)
    let carry = firstKnown ?? 0
    const hours = axis.map((d) => {
        const hi = hoursByDay[d]?.hi
        if (hi != null) carry = hi
        return carry
    })

    // Per-day alarm / DTC counts + fault descriptions for the hover log.
    const faultsByDay: Record<string, ServiceEvent[]> = {}
    events.forEach((e) => {
        if (!e.day) return
        ;(faultsByDay[e.day] = faultsByDay[e.day] || []).push(e)
    })
    const alarms = axis.map(
        (d) => (faultsByDay[d] || []).filter((e) => e.type === 'alarm').length
    )
    const dtcs = axis.map(
        (d) => (faultsByDay[d] || []).filter((e) => e.type === 'dtc').length
    )

    const hasHours = axis.some((d) => hoursByDay[d]?.hi != null)

    return (
        <Box>
            <Box sx={{ maxWidth: 520 }}>
                <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', fontWeight: 600 }}
                >
                    Engine hours &amp; faults · last {axis.length} days
                </Typography>
                {hasHours ? (
                    <Box sx={{ mt: 1.5 }}>
                        <Sparkline
                            data={hours}
                            color={ENGINE_COLOR}
                            days={axis}
                            alarms={alarms}
                            dtcs={dtcs}
                            fmt={(v) => `${v.toLocaleString()} h`}
                            tipExtra={(i) => {
                                const d = axis[i]
                                const hd = hoursByDay[d]
                                const log = faultsByDay[d] || []
                                return (
                                    <>
                                        {hd ? (
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'block',
                                                    fontSize: 10,
                                                    color: 'rgba(255,255,255,0.7)',
                                                }}
                                            >
                                                {hd.lo.toLocaleString()}–
                                                {hd.hi.toLocaleString()} h that
                                                day
                                            </Box>
                                        ) : (
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'block',
                                                    fontSize: 10,
                                                    color: 'rgba(255,255,255,0.45)',
                                                }}
                                            >
                                                no telemetry that day
                                            </Box>
                                        )}
                                        {log.slice(0, 5).map((e, k) => (
                                            <Box
                                                key={k}
                                                component="span"
                                                sx={{
                                                    display: 'block',
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                    color:
                                                        e.type === 'alarm'
                                                            ? ALARM_COLOR
                                                            : DTC_COLOR,
                                                }}
                                            >
                                                • {e.description}
                                            </Box>
                                        ))}
                                        {log.length > 5 && (
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'block',
                                                    fontSize: 10,
                                                    color: 'rgba(255,255,255,0.55)',
                                                }}
                                            >
                                                +{log.length - 5} more
                                            </Box>
                                        )}
                                    </>
                                )
                            }}
                        />
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                mt: 0.5,
                            }}
                        >
                            <Typography
                                sx={{ fontSize: 10, color: 'text.disabled' }}
                            >
                                {dayjs(axis[0]).format('MMM D')}
                            </Typography>
                            <Typography
                                sx={{ fontSize: 10, color: 'text.disabled' }}
                            >
                                {dayjs(axis[axis.length - 1]).format('MMM D')}
                            </Typography>
                        </Box>
                    </Box>
                ) : (
                    <Typography
                        variant="body2"
                        sx={{ display: 'block', mt: 1.5, color: 'text.disabled' }}
                    >
                        No engine-hour telemetry in this period.
                    </Typography>
                )}
            </Box>
        </Box>
    )
}

export default MetricsPanel
